/**
 * Notification sound engine for the new alert-firehose / broadcast-firehose
 * real-time system.
 *
 * Two distinct sonic identities:
 *   • adminAlert()       — emergency-room style: 3 sharp pulses + low rumble
 *                          designed to cut through ambient dispatch noise.
 *   • citizenBroadcast() — civil-defence siren: rising 600→1100 Hz sweep,
 *                          softer attack so it doesn't startle a citizen
 *                          looking at their phone.
 *
 * Volume is read from `notificationAudio.getVolume()` each play, so a
 * settings drawer toggle / slider takes effect on the next event without
 * needing to bus a callback through every hook.
 *
 * Persistence
 * -----------
 * Settings live in localStorage under `rr_admin_audio` (admin) and
 * `rr_citizen_audio` (citizen):
 *   { enabled: bool, volume: 0..1, lastUnlocked: ms }
 *
 * Browsers won't let us play audio until a user gesture occurs in the
 * tab. `unlock()` should be called from a click handler the first time
 * the admin logs in or the citizen onboards — see `useAdminFirehose` and
 * `useCitizenBroadcasts` where we attach a one-time click listener.
 */

const ADMIN_KEY = "rr_admin_audio";
const CITIZEN_KEY = "rr_citizen_audio";

let ctx = null;
let unlocked = false;

const ensureCtx = () => {
  if (ctx) return ctx;
  try {
    const Klass = window.AudioContext || window.webkitAudioContext;
    if (!Klass) return null;
    ctx = new Klass();
    return ctx;
  } catch (_e) {
    return null;
  }
};

const readSettings = (key) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return { enabled: true, volume: 0.8 };
    const parsed = JSON.parse(raw);
    return {
      enabled: parsed.enabled !== false,
      volume: typeof parsed.volume === "number" ? Math.max(0, Math.min(1, parsed.volume)) : 0.8,
    };
  } catch (_e) {
    return { enabled: true, volume: 0.8 };
  }
};

const writeSettings = (key, settings) => {
  try {
    localStorage.setItem(key, JSON.stringify(settings));
  } catch (_e) { /* localStorage may be unavailable in private mode */ }
};

const tone = (freq, startAt, durMs, peak = 0.6, type = "sine") => {
  if (!ctx) return;
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = type;
  osc.frequency.value = freq;
  const t0 = ctx.currentTime + startAt;
  gain.gain.setValueAtTime(0, t0);
  gain.gain.linearRampToValueAtTime(peak, t0 + 0.01);
  gain.gain.linearRampToValueAtTime(0, t0 + durMs / 1000);
  osc.connect(gain).connect(ctx.destination);
  osc.start(t0);
  osc.stop(t0 + durMs / 1000 + 0.02);
};

const sweep = (fromHz, toHz, startAt, durMs, peak = 0.55, type = "sawtooth") => {
  if (!ctx) return;
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = type;
  const t0 = ctx.currentTime + startAt;
  osc.frequency.setValueAtTime(fromHz, t0);
  osc.frequency.linearRampToValueAtTime(toHz, t0 + durMs / 1000);
  gain.gain.setValueAtTime(0, t0);
  gain.gain.linearRampToValueAtTime(peak, t0 + 0.02);
  gain.gain.linearRampToValueAtTime(0, t0 + durMs / 1000);
  osc.connect(gain).connect(ctx.destination);
  osc.start(t0);
  osc.stop(t0 + durMs / 1000 + 0.02);
};

export const notificationAudio = {
  isUnlocked() { return unlocked; },

  async unlock() {
    const c = ensureCtx();
    if (!c) return false;
    try {
      if (c.state === "suspended") await c.resume();
      unlocked = true;
      return true;
    } catch (_e) {
      return false;
    }
  },

  // ------------------------------------------------------------------
  // Admin
  // ------------------------------------------------------------------
  getAdminSettings() { return readSettings(ADMIN_KEY); },
  setAdminSettings(next) {
    const current = readSettings(ADMIN_KEY);
    writeSettings(ADMIN_KEY, { ...current, ...next });
  },
  /**
   * Three crisp 1.2 kHz pulses + a low 220 Hz pad. Cuts through a noisy
   * dispatch room without sounding alarmist.
   */
  playAdminAlert() {
    const c = ensureCtx();
    if (!c || !unlocked) return;
    const s = readSettings(ADMIN_KEY);
    if (!s.enabled || s.volume <= 0) return;
    const v = s.volume;
    try {
      // Three sharp triangular pulses
      tone(1180, 0.00, 90, 0.55 * v, "triangle");
      tone(1180, 0.16, 90, 0.55 * v, "triangle");
      tone(1180, 0.32, 90, 0.55 * v, "triangle");
      // Low background rumble for gravitas
      tone(220, 0.05, 350, 0.3 * v, "sine");
    } catch (e) { void e; }
  },

  // ------------------------------------------------------------------
  // Citizen
  // ------------------------------------------------------------------
  getCitizenSettings() { return readSettings(CITIZEN_KEY); },
  setCitizenSettings(next) {
    const current = readSettings(CITIZEN_KEY);
    writeSettings(CITIZEN_KEY, { ...current, ...next });
  },
  /**
   * Rising civil-defence sweep: 600 → 1100 Hz then back. Two cycles.
   */
  playCitizenBroadcast() {
    const c = ensureCtx();
    if (!c || !unlocked) return;
    const s = readSettings(CITIZEN_KEY);
    if (!s.enabled || s.volume <= 0) return;
    const v = s.volume;
    try {
      sweep(600, 1100, 0.00, 350, 0.5 * v, "sawtooth");
      sweep(1100, 600, 0.36, 350, 0.5 * v, "sawtooth");
    } catch (e) { void e; }
  },
};
